import { Box, Paper, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { ChartDataset, Point } from "chart.js";
import "chart.js/auto";
import { useMemo } from "react";
import { Line } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import { useCurrency } from "../../context/CurrencyContext.tsx";
import { useTheme } from "../../context/ThemeContext";
import { ShownOutcome } from "../../shared/adjustedView";
import { getTooltip } from "../../utils/chartjsTheming";
import { SectionLabel } from "../general/SectionLabel";
import {
  RISK_CHART_HEIGHT,
  RISK_LINE_POINTS,
  riskChartLegend,
  riskChartScales,
  settlementLegendEntry,
  settlementShade,
  SETTLEMENT_SHADING,
} from "./riskChart";

/** How much of its colour the area under the expenses takes. */
const EXPENSES_AREA_ALPHA = 0.28;

export const LawFirmRiskAnalysis = ({
  outcomes,
}: {
  outcomes: ShownOutcome[];
}) => {
  const { theme } = useTheme();
  const { currency } = useCurrency();
  const { t } = useTranslation(undefined, {
    keyPrefix: "lawFirmRiskAnalysis",
  });
  const { positive, negative } = theme.palette.data;

  const {
    incomeDataset,
    expensesDataset,
    bands,
  }: {
    incomeDataset: ChartDataset<"line", Point[]>;
    expensesDataset: ChartDataset<"line", Point[]>;
    bands: { from: number; to: number }[];
  } = useMemo(() => {
    const incomeDataset: ChartDataset<"line", Point[]> = {
      label: t("income"),
      data: [],
      stepped: true,
      fill: false,
      // a line has no area: its box in the legend stays empty
      backgroundColor: "transparent",
      borderColor: positive.main,
      borderWidth: 2.5,
      ...RISK_LINE_POINTS,
      order: 2,
    };
    const expensesDataset: ChartDataset<"line", Point[]> = {
      label: t("expense"),
      data: [],
      stepped: true,
      fill: true,
      backgroundColor: alpha(negative.main, EXPENSES_AREA_ALPHA),
      borderColor: negative.main,
      borderWidth: 2,
      ...RISK_LINE_POINTS,
      order: 1,
    };
    const lawFirmOutcomes: {
      probability: number;
      income: number;
      expenses: number;
      settled: boolean;
    }[] = outcomes
      // a firm whose costs are unknown has no expenses to draw
      .flatMap(({ probability, lawFirm, settledAt }) =>
        lawFirm.costs === "known"
          ? [
              {
                probability,
                expenses: lawFirm.expenses,
                income: lawFirm.income,
                settled: settledAt !== undefined,
              },
            ]
          : [],
      )
      .sort((o1, o2) => o1.income - o1.expenses - (o2.income - o2.expenses));

    let cumulativeProbability = 0;
    // the stretches the settlements take, shaded behind the lines
    const bands: { from: number; to: number }[] = [];
    for (const { probability, expenses, income, settled } of lawFirmOutcomes) {
      if (settled) {
        const last = bands[bands.length - 1];
        if (last?.to === cumulativeProbability)
          last.to = cumulativeProbability + probability;
        else
          bands.push({
            from: cumulativeProbability,
            to: cumulativeProbability + probability,
          });
      }
      // push income and expense at current cumulative probability
      incomeDataset.data.push({
        x: cumulativeProbability,
        y: income,
      });
      expensesDataset.data.push({
        x: cumulativeProbability,
        y: expenses,
      });
      cumulativeProbability += probability;
    }
    // close data at 100%
    const last = lawFirmOutcomes[lawFirmOutcomes.length - 1];
    if (last !== undefined) {
      incomeDataset.data.push({ x: 1, y: last.income });
      expensesDataset.data.push({ x: 1, y: last.expenses });
    }

    return { incomeDataset, expensesDataset, bands };
  }, [outcomes, t, positive, negative]);
  const shade = settlementShade(theme);

  if (!outcomes.length) {
    return (
      <Typography sx={{ color: "text.secondary" }}>
        {t("noOutcomes")}
      </Typography>
    );
  }

  const datasets: ChartDataset<"line", Point[]>[] = [];
  const incomeDatasetIndex = datasets.push(incomeDataset) - 1;
  const expensesDatasetIndex = datasets.push(expensesDataset) - 1;
  if (bands.length > 0)
    datasets.push(
      settlementLegendEntry(
        t("settlement", { keyPrefix: "case.analytics.risk.chart" }),
        shade,
      ),
    );

  return (
    <Paper variant="outlined" sx={{ borderRadius: 3, px: 2.5, pt: 2, pb: 2 }}>
      <SectionLabel component="h2">{t("title")}</SectionLabel>
      <Box sx={{ height: RISK_CHART_HEIGHT }}>
        <Line
          data={{ datasets }}
          plugins={[SETTLEMENT_SHADING]}
          options={{
            maintainAspectRatio: false,
            plugins: {
              legend: riskChartLegend(theme),
              settlementShading: { bands, color: shade },
              tooltip: {
                ...getTooltip(theme),
                mode: "index",
                axis: "x",
                intersect: false,
                filter: ({ chart, datasetIndex, dataIndex, parsed: { x } }) => {
                  const series = chart.data.datasets[
                    datasetIndex
                  ] as ChartDataset<"line", Point[]>;
                  const firstIndexAtX = series.data.findIndex(
                    (pt) => pt.x === x,
                  );
                  return dataIndex === firstIndexAtX;
                },
                callbacks: {
                  title: () => "",
                  label: ({ dataset, parsed }) =>
                    `${dataset.label}: ${currency.format(parsed.y ?? 0)}`,
                  // profit/loss = income – expenses
                  footer: (items) => {
                    const incomeItem = items.find(
                      ({ datasetIndex }) => datasetIndex === incomeDatasetIndex,
                    );
                    if (incomeItem === undefined) return "";

                    const expensesItem = items.find(
                      ({ datasetIndex }) =>
                        datasetIndex === expensesDatasetIndex,
                    );
                    if (expensesItem === undefined) return "";

                    const netResult =
                      (incomeItem.parsed.y ?? 0) - (expensesItem.parsed.y ?? 0);
                    return t(netResult < 0 ? "loss" : "profit", {
                      value: currency.format(netResult),
                    });
                  },
                },
              },
            },
            scales: riskChartScales({
              theme,
              currency,
              axisTitle: t("axis"),
              y: { beginAtZero: true },
            }),
          }}
        />
      </Box>
    </Paper>
  );
};
